<template>
  <section class="topup-result mt-5">
    <loader v-if="loading" />

    <promised-activated
      v-else-if="promised"
      :days="promisedDays"
      :pending="promisedPending"
      @action="goToOrders"
    />

    <payment-succeeded
      v-else-if="succeeded"
      :amount="payment.amount"
      @action="goToOrders"
    />

    <payment-failed
      v-else-if="failed"
      :reason="payment.failureMessage"
      @action="goToTopup"
    />

    <payment-pending v-else @action="goToOrders" />
  </section>
</template>

<script>
  import { mapState } from 'vuex'
  import Loader from '@/components/loader/Loader'
  import PaymentFailed from '@/components/billing/topup-result/PaymentFailed'
  import PaymentPending from '@/components/billing/topup-result/PaymentPending.vue'
  import PaymentSucceeded from '@/components/billing/topup-result/PaymentSucceeded'
  import PromisedActivated from '@/components/billing/topup-result/PromisedActivated'
  import SnackbarMessage from '@/mixins/snackbar-message'
  import RouteName from '@/models/enums/RouteName'
  import billingApi from '@/api/billing'
  import { websocketService } from '@/services/WebsocketService'
  import { getPaymentAccountChannel } from '@/util/websocket-channels'
  import { CRM_BILLING_PAYMENT_COMPLETED } from '@/constants/websocket-events'
  import { PENDING_PAYMENT_KEY } from '@/constants/billing'

  export default {
    name: 'TopupResult',
    components: {
      Loader,
      PaymentFailed,
      PaymentPending,
      PaymentSucceeded,
      PromisedActivated,
    },
    mixins: [SnackbarMessage],
    data () {
      return {
        loading: true,
        payment: null,
      }
    },
    computed: {
      ...mapState('billing', ['paymentAccount']),
      promised () {
        return this.$route.query.promised === '1'
      },
      promisedDays () {
        return this.paymentAccount.promisedPayment
          ? this.paymentAccount.promisedPayment.termDays
          : 0
      },
      // Отсрочку могли взять заранее: срок пойдёт только когда кончатся деньги
      promisedPending () {
        return !!this.paymentAccount.promisedPayment && this.paymentAccount.promisedPayment.pending
      },
      succeeded () {
        return this.payment !== null && this.payment.status === 'succeeded'
      },
      failed () {
        return this.payment !== null && this.payment.status !== 'succeeded' && this.payment.status !== 'pending'
      },
    },
    async created () {
      try {
        await this.$store.dispatch('billing/fetchPaymentAccount', { force: true })

        if (!this.promised) {
          this.listen()
          // Вебхук мог прийти, пока клиент возвращался с ЮKassa: разовая
          // проверка на случай, если событие ушло до подписки.
          await this.loadPayment()
        }
      } catch (err) {
        this.showErrorFromResponse(err)
      } finally {
        this.loading = false
      }
    },
    beforeDestroy () {
      if (this.promised) return

      const channel = this.channel()
      if (channel) {
        channel.stopListening(CRM_BILLING_PAYMENT_COMPLETED, this.onPaymentCompleted)
      }
    },
    methods: {
      paymentId () {
        return this.$route.query.payment || sessionStorage.getItem(PENDING_PAYMENT_KEY)
      },
      // Локально echo-server поднят без авторизации — там канал публичный
      channel () {
        if (!this.paymentAccount.id) return null
        const echo = websocketService.getEcho()
        const name = getPaymentAccountChannel(this.paymentAccount.id)

        return process.env.VUE_APP_MODE === 'local' ? echo.channel(name) : echo.private(name)
      },
      listen () {
        const channel = this.channel()
        if (channel) {
          channel.listen(CRM_BILLING_PAYMENT_COMPLETED, this.onPaymentCompleted)
        }
      },
      async loadPayment () {
        const id = this.paymentId()
        if (!id) return

        const payment = await billingApi.fetchPayment(id)
        if (payment.status !== 'pending') this.finish(payment)
      },
      onPaymentCompleted (payload) {
        const id = this.paymentId()
        // Событие приходит на весь счёт: чужие платежи этой страницы не касаются
        if (id && String(payload.paymentId) !== String(id)) return

        this.finish(payload)
      },
      finish (payment) {
        this.payment = payment
        sessionStorage.removeItem(PENDING_PAYMENT_KEY)
        this.$store.dispatch('billing/fetchPaymentAccount', { force: true }).catch(() => {})
      },
      goToOrders () {
        this.$router.push('/orders')
      },
      goToTopup () {
        this.$router.push({ name: RouteName.BillingTopup })
      },
    },
  }
</script>

<style lang="less" scoped>
.topup-result {
  padding-left: 4px;
  padding-right: 4px;
}
</style>
